/**
 * Voices settings list — Agents as Users Phase 1.
 * Gated by useAgentsAsUsersEnabled. Zero/one/few/many + search at 8+.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Mic2, Plus, ChevronRight, Search, ChevronLeft } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getItemCountState } from '../../../components/collection/types';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { useAgentsAsUsersEnabled } from '../../../features/voices/hooks/useAgentsAsUsersEnabled';
import { VoiceBadge } from '../../../features/voices/components/VoiceBadge';
import { useVoicesStore } from '../../../features/voices/voicesStore';
import type { VoiceCorpusKind, VoiceSeatType } from '../../../features/voices/types';
import { useUserStore } from '../../../stores/prioritization/userStore';

export default function VoicesPage() {
  const enabled = useAgentsAsUsersEnabled();
  const workspaceId = getCurrentWorkspaceId();
  const voices = useVoicesStore((s) => s.voices);
  const fetchVoices = useVoicesStore((s) => s.fetchVoices);
  const saveVoice = useVoicesStore((s) => s.saveVoice);
  const isLoading = useVoicesStore((s) => s.isLoading);
  const error = useVoicesStore((s) => s.error);
  const userId = useUserStore((s) => s.userId);
  const { users } = useSystemUsers({ workspaceId });

  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sponsorId, setSponsorId] = useState('');
  const [corpusKind, setCorpusKind] = useState<VoiceCorpusKind>('support_tickets');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (enabled && workspaceId) void fetchVoices(workspaceId);
  }, [enabled, workspaceId, fetchVoices]);

  useEffect(() => {
    if (!sponsorId && (userId || users[0]?.id)) {
      setSponsorId(userId ?? users[0]?.id ?? '');
    }
  }, [sponsorId, userId, users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return voices;
    return voices.filter(
      (v) =>
        v.displayName.toLowerCase().includes(q) ||
        v.slug.toLowerCase().includes(q) ||
        (v.description ?? '').toLowerCase().includes(q),
    );
  }, [voices, search]);

  const countState = getItemCountState(filtered.length);

  if (!enabled) {
    return <Navigate to="/prioritization/settings/agents" replace />;
  }

  const handleCreate = async () => {
    if (!workspaceId || !name.trim() || !sponsorId) return;
    setSaving(true);
    const seats: VoiceSeatType[] = ['constituency'];
    await saveVoice({
      workspaceId,
      displayName: name.trim(),
      description: description.trim() || null,
      sponsorUserId: sponsorId,
      participationSeats: seats,
      corpusKind,
      status: 'active',
    });
    setSaving(false);
    setShowCreate(false);
    setName('');
    setDescription('');
    await fetchVoices(workspaceId);
  };

  const sponsorName = (id: string) => {
    const u = users.find((x) => x.id === id);
    return u?.name ?? u?.email ?? id.slice(0, 8);
  };

  return (
    <div className="space-y-6">
      <Link
        to="/prioritization/settings/agents"
        className="inline-flex items-center gap-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)] no-underline"
      >
        <ChevronLeft className="h-3.5 w-3.5" /> Back to Agents
      </Link>

      <PageHeader
        title="Voices"
        subtitle="Participant-standing agents — voice, never authority. Advisory contributors with a human sponsor."
        actions={
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            Add Voice
          </button>
        }
      />

      {error && (
        <div className="border border-[var(--coral)] bg-[var(--coral-dim)] px-3 py-2 text-sm text-[var(--coral)]">{error}</div>
      )}

      {countState === 'many' && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-charcoal-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Voices…"
            className="w-full border border-charcoal-200 py-2 pl-9 pr-3 text-sm"
          />
        </div>
      )}

      {isLoading && <p className="text-sm text-charcoal-500">Loading Voices…</p>}

      {!isLoading && countState === 'zero' && (
        <div className="border border-dashed border-charcoal-200 bg-[var(--bg-raised)] p-10 text-center">
          <Mic2 className="mx-auto h-8 w-8 text-electric-mustard-600" />
          <h3 className="mt-3 font-heading text-lg font-semibold text-charcoal-900">No Voices yet</h3>
          <p className="mt-1 text-sm text-charcoal-500">
            Add a Customer Voice or Revenue Voice so a constituency can contribute to async sessions without holding authority.
          </p>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="mt-4 inline-flex items-center gap-1.5 bg-primary-600 px-3 py-2 text-sm font-medium text-white"
          >
            <Plus className="h-4 w-4" />
            Add Voice
          </button>
        </div>
      )}

      {!isLoading && countState !== 'zero' && (
        <div className={countState === 'one' ? 'max-w-xl space-y-3' : 'space-y-3'}>
          {filtered.map((v) => (
            <Link
              key={v.id}
              to={`/prioritization/settings/voices/${v.id}`}
              className="card flex items-start gap-4 no-underline transition-all hover:border-electric-mustard-300"
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center bg-electric-mustard-100 text-electric-mustard-700">
                <Mic2 className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-charcoal-900">{v.displayName}</span>
                  <VoiceBadge label={v.badgeLabel} />
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${
                      v.status === 'active' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-charcoal-100 text-charcoal-500'
                    }`}
                  >
                    {v.status}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-charcoal-500">
                  Sponsor: {sponsorName(v.sponsorUserId)}
                  {v.corpusKind ? ` · ${v.corpusKind === 'support_tickets' ? 'Support tickets' : 'CRM pipeline'}` : ''}
                  {v.participationSeats.length ? ` · Seats: ${v.participationSeats.join(', ')}` : ''}
                </p>
                {v.description && <p className="mt-1 text-xs leading-relaxed text-charcoal-500">{v.description}</p>}
              </div>
              <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-charcoal-400" />
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md border border-charcoal-200 bg-[var(--bg-raised)] p-6 shadow-lg">
            <h3 className="font-heading text-lg font-semibold text-charcoal-900">Add Voice</h3>
            <p className="mt-1 text-sm text-charcoal-500">
              Voices contribute to judgment; humans keep authority.
            </p>
            <div className="mt-4 space-y-3">
              <label className="block text-sm">
                <span className="font-medium text-charcoal-700">Name</span>
                <input
                  className="mt-1 w-full border border-charcoal-200 px-3 py-2 text-sm"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Customer Voice"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-charcoal-700">Description</span>
                <textarea
                  className="mt-1 w-full border border-charcoal-200 px-3 py-2 text-sm"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-charcoal-700">Sponsor</span>
                <select
                  className="mt-1 w-full border border-charcoal-200 px-3 py-2 text-sm"
                  value={sponsorId}
                  onChange={(e) => setSponsorId(e.target.value)}
                >
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name ?? u.email}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="font-medium text-charcoal-700">Corpus</span>
                <select
                  className="mt-1 w-full border border-charcoal-200 px-3 py-2 text-sm"
                  value={corpusKind}
                  onChange={(e) => setCorpusKind(e.target.value as VoiceCorpusKind)}
                >
                  <option value="support_tickets">Support tickets</option>
                  <option value="crm_pipeline">CRM / sales pipeline</option>
                </select>
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 text-sm text-charcoal-600 hover:bg-charcoal-50"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving || !name.trim() || !sponsorId}
                onClick={() => void handleCreate()}
                className="bg-primary-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
